import { useCallback, useEffect, useRef, useState } from 'react'

import { getIdToken } from 'firebase/auth'

import { auth } from '@/firebase'

type TranscriptionProvider = 'deepgram' | 'openai' | 'assemblyai'

export interface TranscriptSegment {
  text: string
  speaker: number | null
}

interface UseLiveTranscriptionOptions {
  interviewId: string
  onTranscript?: (transcript: string, isFinal: boolean) => void
  preferredProvider?: TranscriptionProvider
}

interface TranscriptionState {
  isConnected: boolean
  provider: TranscriptionProvider | null
  segments: TranscriptSegment[]
  interimSegment: TranscriptSegment | null
  error: string | null
}

function buildWebSocketUrl(
  interviewId: string,
  token: string,
  preferredProvider?: TranscriptionProvider,
): string {
  const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8080'
  const wsUrl = backendUrl
    .replace('http://', 'ws://')
    .replace('https://', 'wss://')
    .replace('/api', '')

  const params = new URLSearchParams({
    token,
    interviewId,
    ...(preferredProvider && { provider: preferredProvider }),
  })

  return `${wsUrl}/ws/live-transcription?${params}`
}

export function useLiveTranscription({
  interviewId,
  onTranscript,
  preferredProvider,
}: UseLiveTranscriptionOptions) {
  const [state, setState] = useState<TranscriptionState>({
    isConnected: false,
    provider: null,
    segments: [],
    interimSegment: null,
    error: null,
  })

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<number | null>(null)
  const pendingChunksRef = useRef<ArrayBuffer[]>([])
  const isFlushingRef = useRef(false)
  const onTranscriptRef = useRef(onTranscript)

  useEffect(() => {
    onTranscriptRef.current = onTranscript
  }, [onTranscript])

  const flushPendingChunks = useCallback(() => {
    if (isFlushingRef.current) return

    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return

    isFlushingRef.current = true
    try {
      const chunks = pendingChunksRef.current
      pendingChunksRef.current = []
      for (const chunk of chunks) {
        ws.send(chunk)
      }
    } finally {
      isFlushingRef.current = false
    }
  }, [])

  const handleMessage = useCallback(
    (
      event: MessageEvent,
      resolve: (provider: TranscriptionProvider | null) => void,
    ) => {
      try {
        const data = JSON.parse(event.data) as {
          status?: string
          provider?: TranscriptionProvider
          transcript?: string
          isFinal?: boolean
          speaker?: number | null
          error?: string
        }

        if (data.status === 'connected') {
          const connectedProvider = data.provider ?? null
          setState((prev) => ({
            ...prev,
            isConnected: true,
            error: null,
            provider: connectedProvider,
          }))
          resolve(connectedProvider)
          return
        }

        if (data.error) {
          setState((prev) => ({ ...prev, error: data.error ?? null }))
          return
        }

        if (data.transcript) {
          const { transcript, isFinal = false, speaker = null } = data
          if (isFinal) {
            setState((prev) => ({
              ...prev,
              segments: [...prev.segments, { text: transcript, speaker }],
              interimSegment: null,
            }))
          } else {
            setState((prev) => ({
              ...prev,
              interimSegment: { text: transcript, speaker },
            }))
          }
          onTranscriptRef.current?.(transcript, isFinal)
        }
      } catch (err) {
        console.error('Failed to parse WebSocket message:', err)
      }
    },
    [],
  )

  const handleError = useCallback(
    (
      event: Event,
      resolve: (provider: TranscriptionProvider | null) => void,
    ) => {
      console.error('WebSocket error:', event)
      setState((prev) => ({ ...prev, error: 'Connection error' }))
      pendingChunksRef.current = []
      resolve(null)
    },
    [],
  )

  const handleClose = useCallback(() => {
    setState((prev) => ({ ...prev, isConnected: false }))
    wsRef.current = null
    pendingChunksRef.current = []
  }, [])

  const handleOpen = useCallback(() => {
    setState((prev) => ({ ...prev, isConnected: true, error: null }))
    void flushPendingChunks()
  }, [flushPendingChunks])

  const connect =
    useCallback(async (): Promise<TranscriptionProvider | null> => {
      if (!interviewId) return null

      if (
        wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING
      ) {
        return state.provider
      }

      const currentUser = auth.currentUser
      if (!currentUser) {
        setState((prev) => ({ ...prev, error: 'Not authenticated' }))
        return null
      }

      try {
        const token = await getIdToken(currentUser)
        const url = buildWebSocketUrl(interviewId, token, preferredProvider)
        const ws = new WebSocket(url)

        return new Promise((resolve) => {
          ws.onopen = handleOpen
          ws.onmessage = (event) => handleMessage(event, resolve)
          ws.onerror = (event) => handleError(event, resolve)
          ws.onclose = handleClose
          wsRef.current = ws
        })
      } catch (err) {
        console.error('Failed to connect to live transcription:', err)
        setState((prev) => ({ ...prev, error: 'Failed to connect' }))
        return null
      }
    }, [
      interviewId,
      state.provider,
      preferredProvider,
      handleOpen,
      handleMessage,
      handleError,
      handleClose,
    ])

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }

    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }

    pendingChunksRef.current = []
    setState((prev) => ({ ...prev, isConnected: false }))
  }, [])

  const sendAudioChunk = useCallback(
    (chunk: ArrayBuffer) => {
      if (!wsRef.current) return
      pendingChunksRef.current.push(chunk)
      flushPendingChunks()
    },
    [flushPendingChunks],
  )

  const clearTranscript = useCallback(() => {
    setState((prev) => ({ ...prev, segments: [], interimSegment: null }))
  }, [])

  useEffect(() => {
    return () => {
      disconnect()
    }
  }, [disconnect])

  return {
    isConnected: state.isConnected,
    provider: state.provider,
    segments: state.segments,
    interimSegment: state.interimSegment,
    error: state.error,
    connect,
    disconnect,
    sendAudioChunk,
    clearTranscript,
  }
}
